import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { acceptConsent, fetchConsent } from "../lib/api";
import LegalDoc from "./LegalDoc";
import { AlertTriangle, ArrowRight, Check, ChevronDown, Globe, Lock } from "lucide-react";

/** Blocking gate. Nothing is asked of the subject until all three documents are accepted,
 *  and a material revision brings them back here mid-interview rather than erroring. */
export const ConsentStep = ({ onAccepted }) => {
  const [d, setD] = useState(null);
  const [ticked, setTicked] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchConsent()
      .then((res) => {
        setD(res);
        if (res.consent?.accepted && !res.blocked) onAccepted(res.consent);
      })
      .catch(() => setErr("Could not load the statement. Reload the page."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accept = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await acceptConsent(
        d.manifest_version,
        d.legal.bundle_version,
        d.legal.documents.map((x) => x.id)
      );
      onAccepted(res.consent);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not record that. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!d) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="label">{err || "Loading the statement…"}</p>
      </div>
    );
  }

  const s = d.summary;
  const docs = d.legal.documents;
  const all = docs.every((x) => ticked[x.id]);
  const revision = d.consent?.must_reaccept ? d.consent.revision : null;

  return (
    <div
      data-testid="consent-step"
      className="thin-scroll min-h-0 flex-1 overflow-y-auto px-6 py-14 sm:px-10"
    >
      <div className="mx-auto max-w-2xl">
        <p className="label">
          {revision ? "The agreement has been revised" : "Before the first question"} · bundle v
          {d.legal.bundle_version}
        </p>
        <h1 className="mt-5 font-display text-3xl leading-[1.02] tracking-tight sm:text-4xl">
          {revision ? (
            <>
              Something material
              <br />
              <span className="italic text-zinc-400">changed. Read it.</span>
            </>
          ) : (
            <>
              This instrument reads you
              <br />
              <span className="italic text-zinc-400">closely. Here is the deal.</span>
            </>
          )}
        </h1>

        {revision && (
          <div
            data-testid="consent-revision"
            className="mt-8 border border-zinc-600 px-5 py-5"
          >
            <p className="label flex items-center gap-2">
              <AlertTriangle size={11} strokeWidth={1.7} />
              v{revision.from_version || "your original acceptance"} → v{revision.to_version}
            </p>
            <ul className="mt-4 space-y-2.5">
              {revision.changes.map((c) => (
                <li key={c} className="text-sm leading-relaxed text-zinc-200">
                  — {c}
                </li>
              ))}
            </ul>
            <p className="mt-5 font-data text-[10px] leading-relaxed text-zinc-500">
              Your transcript, your map and your scores are untouched. Nothing new has been done
              with your answers, and nothing will be until you accept this.
            </p>
          </div>
        )}

        <p className="mt-7 border-l border-[var(--line-hi)] pl-5 text-base leading-relaxed text-zinc-200">
          {d.headline}
        </p>

        <div className="mt-12 space-y-10">
          <div>
            <p className="label">What is recorded</p>
            <ul className="mt-4 space-y-2.5" data-testid="consent-collected">
              {s.collected.map((c) => (
                <li key={c} className="text-sm leading-relaxed text-zinc-300">
                  — {c}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="label">What leaves this system</p>
            <ul className="mt-4 space-y-5" data-testid="consent-outbound">
              {s.leaves_the_system.map((o) => (
                <li key={o.to} className="border-l border-[var(--line)] pl-5">
                  <p className="flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.16em] text-zinc-300">
                    <Globe size={11} strokeWidth={1.6} className="text-zinc-600" />
                    {o.to}
                    <span className="label ml-1">{o.avoidable ? "avoidable" : "required"}</span>
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{o.what}</p>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="label">What never happens</p>
            <ul className="mt-4 space-y-2.5" data-testid="consent-never">
              {s.never.map((n) => (
                <li key={n} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                  <Lock size={11} strokeWidth={1.6} className="mt-1 shrink-0 text-zinc-600" />
                  {n}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="label">What you keep control of</p>
            <ul className="mt-4 space-y-2.5" data-testid="consent-controls">
              {s.your_controls.map((c) => (
                <li key={c} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                  <Check size={11} strokeWidth={1.8} className="mt-1 shrink-0 text-zinc-500" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-[var(--line)] pt-10">
          <p className="label">The agreement · {d.legal.entity}</p>
          <p className="mt-3 max-w-xl text-xs leading-relaxed text-zinc-500">
            Three documents, governed by the law of {d.legal.jurisdiction}. Open each one, read it,
            and tick it. They are also at{" "}
            <Link to="/legal" className="underline decoration-[var(--line-hi)] underline-offset-2">
              /legal
            </Link>{" "}
            whenever you want them.
          </p>

          <div className="mt-7 divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {docs.map((doc) => (
              <div key={doc.id} data-testid={`consent-doc-${doc.id}`}>
                <div className="flex items-start gap-4 py-5">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={!!ticked[doc.id]}
                    data-testid={`consent-tick-${doc.id}`}
                    onClick={() => setTicked((t) => ({ ...t, [doc.id]: !t[doc.id] }))}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${
                      ticked[doc.id]
                        ? "border-zinc-100 bg-zinc-100 text-[var(--void)]"
                        : "border-[var(--line-hi)] text-transparent hover:border-zinc-500"
                    }`}
                  >
                    <Check size={12} strokeWidth={2.4} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug text-zinc-100">
                      I have read and accept the {doc.title}
                      <span className="label ml-3">v{doc.version}</span>
                    </p>
                    <button
                      type="button"
                      data-testid={`consent-open-${doc.id}`}
                      onClick={() => setOpen((o) => (o === doc.id ? null : doc.id))}
                      className="label mt-2 flex items-center gap-1.5 hover:text-zinc-200"
                    >
                      <ChevronDown
                        size={11}
                        strokeWidth={1.8}
                        className={open === doc.id ? "rotate-180 transition-transform" : "transition-transform"}
                      />
                      {open === doc.id ? "close" : "read it here"}
                    </button>
                  </div>
                </div>
                {open === doc.id && (
                  <div className="thin-scroll max-h-[22rem] overflow-y-auto border-t border-[var(--line)] bg-[#08080b] px-5 py-7">
                    <LegalDoc doc={doc} compact />
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            data-testid="consent-accept-btn"
            disabled={busy || !all}
            onClick={accept}
            className="btn-stark mt-10 flex items-center gap-3 !bg-zinc-100 !px-6 !py-3.5 !text-[var(--void)] disabled:opacity-30"
          >
            {busy
              ? "Recording…"
              : all
                ? revision
                  ? "I accept the revision. Carry on."
                  : "I have read this. Begin."
                : `Tick all three to continue (${docs.filter((x) => ticked[x.id]).length}/3)`}
            <ArrowRight size={13} strokeWidth={2} />
          </button>
          <p className="mt-5 font-data text-[10px] leading-relaxed text-zinc-600">
            Accepting records the version and fingerprint of each document against your engagement,
            in order. You can download a signed receipt of it afterwards, it is emailed to you when
            your results are released, and you can delete everything at any point.
          </p>
          <Link
            to="/privacy"
            data-testid="consent-full-statement-link"
            className="label mt-5 inline-block underline decoration-[var(--line-hi)] underline-offset-4 hover:text-zinc-200"
          >
            Read the operational privacy statement in full first →
          </Link>
          {err && (
            <p data-testid="consent-error" className="mt-5 font-data text-[11px] text-zinc-300">
              — {err}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentStep;
